// Click and lead tracking.
//
// Two responsibilities:
// 1. Build trackable share/referral URLs with UTM params + a referral_id
// 2. Submit captured emails to HubSpot via the public Forms API
//
// CONFIG: see .env.example. Vite injects env vars prefixed with VITE_.

const APP_BASE_URL = import.meta.env.VITE_APP_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

// ---- Referral ID handling ----

const REFERRAL_KEY = 'quiz_referral_id_v1';

function generateReferralId() {
  const random = Math.random().toString(36).slice(2, 8);
  const time = Date.now().toString(36).slice(-4);
  return `${time}${random}`;
}

export function getOrCreateReferralId() {
  try {
    let id = localStorage.getItem(REFERRAL_KEY);
    if (!id) {
      id = generateReferralId();
      localStorage.setItem(REFERRAL_KEY, id);
    }
    return id;
  } catch {
    return generateReferralId();
  }
}

const REFERRER_KEY = 'quiz_referrer_id_v1';

export function captureIncomingReferral() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const refId = params.get('ref') || params.get('referral_id');
  if (refId) {
    try { localStorage.setItem(REFERRER_KEY, refId); } catch {}
    track('referral_landed', { referrer_id: refId, source: params.get('utm_source') || 'direct' });
    return refId;
  }
  return null;
}

export function getReferrerId() {
  try { return localStorage.getItem(REFERRER_KEY); } catch { return null; }
}

// ---- URL builders ----

export function buildShareUrl({ medium = 'share', campaign = 'states_quiz', source = '' } = {}) {
  const refId = getOrCreateReferralId();
  const url = new URL(APP_BASE_URL || 'https://quiz.opened.co');
  url.searchParams.set('ref', refId);
  url.searchParams.set('utm_source', source || medium);
  url.searchParams.set('utm_medium', medium);
  url.searchParams.set('utm_campaign', campaign);
  return url.toString();
}

export function buildEmailShareUrl({ score, total } = {}) {
  const url = buildShareUrl({ medium: 'email', source: 'friend_referral', campaign: 'states_quiz_general' });
  const haveScore = score !== undefined && total !== undefined;
  const subject = encodeURIComponent(
    haveScore
      ? `I got ${score} out of ${total} on the OpenEd State Capital Quiz`
      : `How well do you know your state capitals?`
  );
  const body = encodeURIComponent(
    haveScore
      ? `Hey,\n\nJust took the OpenEd State Capital Quiz — got ${score} out of ${total} correct. Curious how you'd do.\n\n${url}\n\n— `
      : `Hey,\n\nFound this fun state capital quiz from OpenEd. Curious how you'd do.\n\n${url}\n\n— `
  );
  return `mailto:?subject=${subject}&body=${body}`;
}

export function buildSocialShareUrl(platform) {
  const url = buildShareUrl({ medium: 'social', source: platform });
  const text = encodeURIComponent("How well do you know your states? Just took this quiz from @openedhq — way harder than I expected.");
  switch (platform) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    case 'twitter':
    case 'x':
      return `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    default:
      return url;
  }
}

// ---- Event tracking ----

export function track(event, props = {}) {
  const payload = {
    event,
    referral_id: (() => { try { return localStorage.getItem(REFERRAL_KEY); } catch { return null; } })(),
    referrer_id: getReferrerId(),
    timestamp: Date.now(),
    ...props,
  };
  if (typeof window !== 'undefined') {
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }
    if (window._hsq) {
      window._hsq.push(['trackCustomBehavioralEvent', { name: `quiz_${event}`, properties: props }]);
    }
  }
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('[track]', event, payload);
  }
  return payload;
}

// ---- Lead capture ----

// Calls our Vercel serverless function /api/submit, which writes to Postgres
// (Vercel Marketplace Neon). Returns the player's share code + referral count
// + CGO unlock state so the UI can render unlock status inline.
export async function submitQuizPlayer({ email, optInToNewsletter = true, state = '', quizStats = {} }) {
  const payload = {
    email,
    optInToNewsletter,
    state,
    referralId: getOrCreateReferralId(),
    referrerId: getReferrerId() || '',
    score: quizStats.score ?? null,
    total: quizStats.total ?? null,
  };

  const res = await fetch('/api/submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // In dev, the serverless function isn't available — log and succeed softly so the UI can be tested.
    if (import.meta.env.DEV) {
      track('email_submitted_dev_stub', { email, optInToNewsletter });
      console.warn('[dev] /api/submit unavailable, succeeded softly:', text.slice(0, 200));
      return {
        success: true,
        devStub: true,
        shareCode: payload.referralId,
        referralsCount: 0,
        threshold: 3,
        cgoUnlocked: false,
      };
    }
    throw new Error(`Submit failed (${res.status}): ${text.slice(0, 200)}`);
  }
  const data = await res.json().catch(() => ({}));
  track('email_submitted', {
    email,
    optInToNewsletter,
    referralsCount: data.referralsCount ?? 0,
    cgoUnlocked: !!data.cgoUnlocked,
  });
  return { success: true, ...data };
}

export async function fetchPlayerStatus(shareCode) {
  if (!shareCode) return null;
  const res = await fetch(`/api/status?shareCode=${encodeURIComponent(shareCode)}`);
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

export async function nativeShare({ title, text, url }) {
  if (typeof navigator === 'undefined' || !navigator.share) return false;
  try {
    await navigator.share({ title, text, url });
    track('shared_via_native', { url });
    return true;
  } catch (e) {
    if (e.name !== 'AbortError') console.warn('native share failed', e);
    return false;
  }
}
